import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { apiFetch, getToken, clearToken, DPP_BASE_URL, API_BASE_URL } from '../dppApi';
import UserAssistant from './UserAssistant';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

// The brand identity strip at the top of each card, rendered exactly like the
// passport's brand header (see Passport.js): the brand's header logo when it has
// one, otherwise the brand title/name in the brand's own colour and font. Keeps
// its own logo-failure state so one broken logo falls back to text on its card
// alone. `p` is a collection item from /api/user/collection.
const CardBrandHeader = ({ p }) => {
    const [logoFailed, setLogoFailed] = useState(false);
    const label = p.brand_title || p.brand_name;
    if (!label && !p.brand_header_logo_url) return null;

    const showLogo = p.brand_header_logo_url && !logoFailed;
    const logo = (
        <img
            className="collection-card-brandhead-logo"
            src={p.brand_header_logo_url}
            alt={label || 'Brand'}
            onError={() => setLogoFailed(true)}
        />
    );

    return (
        <div className="collection-card-brandhead">
            {showLogo ? (
                p.brand_header_logo_bg_color ? (
                    <div
                        className="collection-card-brandhead-logo-wrap"
                        style={{ backgroundColor: p.brand_header_logo_bg_color }}
                    >{logo}</div>
                ) : logo
            ) : (
                <span style={{ color: p.brand_text_color || p.brand_color || undefined,
                               fontFamily: p.brand_font ? `'${p.brand_font}', sans-serif` : undefined }}>
                    {label}
                </span>
            )}
        </div>
    );
};

// A user's collection: every product they currently or previously owned, split
// into Owned and Sold. For sold items a user can still view the documents the
// backend allows them (their chain up to the extent of their ownership, plus
// their own insurance).
const UserCollection = () => {
    const navigate = useNavigate();
    const token = getToken('user');
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [docPanel, setDocPanel] = useState(null);
    const [viewPanel, setViewPanel] = useState(null);

    useEffect(() => {
        if (!token) { navigate('/user'); return; }
        apiFetch('/api/user/collection', { token })
            .then(setItems)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [token, navigate]);

    // Mirror the passport's brand-font loading (Passport.js): inject each brand's
    // font stylesheet so text-based card headers render in the brand's own
    // typeface. Deduped across the collection's brands — CDN fonts (Google/Typekit)
    // load directly, others via the backend font-proxy. Links are cleaned up on
    // unmount / when the collection changes.
    useEffect(() => {
        const seen = new Set();
        const links = [];
        for (const p of items) {
            const fontUrl = p.brand_font_url;
            if (!fontUrl || seen.has(fontUrl)) continue;
            seen.add(fontUrl);
            const isCdn = fontUrl.includes('fonts.googleapis.com') || fontUrl.includes('use.typekit.net');
            let href;
            if (isCdn) {
                href = fontUrl;
            } else {
                const params = new URLSearchParams({ url: fontUrl });
                if (p.brand_font) params.set('family', p.brand_font);
                href = `${API_BASE_URL}/api/font-proxy?${params}`;
            }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
            links.push(link);
        }
        return () => { links.forEach(link => { if (document.head.contains(link)) document.head.removeChild(link); }); };
    }, [items]);

    const logout = () => { clearToken('user'); navigate('/user'); };

    const DOC_META = {
        insurance: { label: 'Upload insurance', fileLabel: 'Insurance document', issuerLabel: 'Insurer', placeholder: "e.g. Lloyd's of London" },
        appraisal: { label: 'Upload appraisal', fileLabel: 'Appraisal document', issuerLabel: 'Appraiser', placeholder: 'e.g. GIA' },
    };

    const openDoc = (productId, docType) =>
        setDocPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeDoc = () => setDocPanel(null);

    const openDocs = async (product) => {
        setViewPanel({ productId: product.product_id, loading: true, docs: [], error: null });
        try {
            const docs = await apiFetch(`/api/passport/${product.qr_code_id}/private-docs`, { token });
            setViewPanel({ productId: product.product_id, loading: false, docs, error: null });
        } catch (err) {
            setViewPanel({ productId: product.product_id, loading: false, docs: [], error: err.message });
        }
    };

    const closeDocs = () => setViewPanel(null);

    const uploadDoc = async () => {
        if (!docPanel.file) return;
        setDocPanel(prev => ({ ...prev, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', docPanel.file);
            form.append('doc_type', docPanel.docType);
            if (docPanel.issuedBy) form.append('issued_by', docPanel.issuedBy);
            if (docPanel.issuedDate) form.append('issued_date', docPanel.issuedDate);
            await apiFetch(`/api/products/${docPanel.productId}/documents/owner`, { method: 'POST', token, body: form, isForm: true });
            setDocPanel(prev => ({ ...prev, busy: false, done: true }));
        } catch (err) {
            setDocPanel(prev => ({ ...prev, busy: false, error: err.message }));
        }
    };

    const renderCard = (p) => (
        <div className="collection-card" key={p.product_id}>
            <CardBrandHeader p={p} />
            {p.image_url && p.image_url.startsWith('http')
                ? <img className="collection-card-img" src={p.image_url} alt={p.name} />
                : <div className="collection-card-img collection-card-img--empty">No image</div>}

            <div className="collection-card-body">
                <div className="collection-card-title">{p.name}</div>

                <div className="collection-card-actions">
                    <a className="collection-action" href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} target="_blank" rel="noreferrer">View passport</a>
                    {p.is_current_owner && Object.keys(DOC_META).map(docType => (
                        <button
                            key={docType}
                            className="collection-action"
                            onClick={() =>
                                docPanel?.productId === p.product_id && docPanel?.docType === docType
                                    ? closeDoc()
                                    : openDoc(p.product_id, docType)
                            }
                        >
                            {docPanel?.productId === p.product_id && docPanel?.docType === docType
                                ? 'Cancel'
                                : DOC_META[docType].label}
                        </button>
                    ))}
                    {!p.is_current_owner && (
                        <button
                            className="collection-action"
                            onClick={() => (viewPanel?.productId === p.product_id ? closeDocs() : openDocs(p))}
                        >
                            {viewPanel?.productId === p.product_id ? 'Hide documents' : 'View my documents'}
                        </button>
                    )}
                </div>

                {docPanel?.productId === p.product_id && (
                    <div className="dpp-transfer-panel">
                        {docPanel.done ? (
                            <p className="dpp-transfer-label" style={{ color: 'green' }}>
                                {DOC_META[docPanel.docType]?.label.replace('Upload', '')} uploaded.
                            </p>
                        ) : (
                            <>
                                <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.fileLabel}</label>
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={e => setDocPanel(prev => ({ ...prev, file: e.target.files[0] || null }))}
                                />
                                <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.issuerLabel} (optional)</label>
                                <input
                                    className="dpp-input"
                                    placeholder={DOC_META[docPanel.docType]?.placeholder}
                                    value={docPanel.issuedBy}
                                    onChange={e => setDocPanel(prev => ({ ...prev, issuedBy: e.target.value }))}
                                />
                                <label className="dpp-transfer-label">Date (optional)</label>
                                <input
                                    className="dpp-input"
                                    type="date"
                                    value={docPanel.issuedDate}
                                    onChange={e => setDocPanel(prev => ({ ...prev, issuedDate: e.target.value }))}
                                />
                                {docPanel.error && <p className="dpp-error">{docPanel.error}</p>}
                                <button
                                    className="dpp-button"
                                    onClick={uploadDoc}
                                    disabled={docPanel.busy || !docPanel.file}
                                >
                                    {docPanel.busy ? 'Uploading…' : 'Upload'}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {!p.is_current_owner && viewPanel?.productId === p.product_id && (
                    <div className="dpp-transfer-panel">
                        {viewPanel.loading && <p className="dpp-muted">Loading…</p>}
                        {viewPanel.error && <p className="dpp-error">{viewPanel.error}</p>}
                        {!viewPanel.loading && !viewPanel.error && viewPanel.docs.length === 0 && (
                            <p className="dpp-muted">No documents.</p>
                        )}
                        {viewPanel.docs.map(d => (
                            <a
                                key={d.doc_id}
                                href={d.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 14, color: '#555' }}
                            >
                                {d.issued_by ? `${d.doc_type} — ${d.issued_by}` : d.doc_type}
                                {d.issued_date ? ` (${d.issued_date})` : ''}
                            </a>
                        ))}
                    </div>
                )}
            </div>
            <QRCodeSVG className="collection-card-qr-corner" value={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} size={56} marginSize={2} />
        </div>
    );

    const owned = items.filter(p => p.is_current_owner);
    const sold = items.filter(p => !p.is_current_owner);

    return (
        <div className="dpp-page">
            <div className="collection-topbar">
                <div className="collection-topbar-brand">
                    <img className="dpp-brand-mark" src={bloomMark} alt="Bloom" />
                    <h1 className="collection-topbar-title">My Collection</h1>
                </div>
                <button className="dpp-link-button" onClick={logout}>Log out</button>
            </div>

            <div className="dpp-dashboard-cols">
                <div className="dpp-dashboard-main">
                    {loading && <p className="collection-status collection-status--flush">Loading…</p>}
                    {error && <p className="dpp-error">{error}</p>}
                    {!loading && !error && items.length === 0 && (
                        <p className="collection-status collection-status--flush">You don't own any products yet.</p>
                    )}

                    {owned.length > 0 && (
                        <div className="collection-grid collection-grid--left">{owned.map(renderCard)}</div>
                    )}
                    {sold.length > 0 && (
                        <>
                            <h2 className="collection-section-title collection-section-title--flush">Previously owned</h2>
                            <div className="collection-grid collection-grid--left">{sold.map(renderCard)}</div>
                        </>
                    )}
                </div>

                <aside className="dpp-dashboard-side">
                    <UserAssistant />
                </aside>
            </div>

            <footer className="site-footer">© {new Date().getFullYear()} Bloom. All rights reserved.</footer>
        </div>
    );
};

export default UserCollection;
